<template>
  <b-form method="POST" @submit.prevent="() => false" class="p-lg-4">
    <b-row>
      <b-col v-if="!showFullForm">

        <!-- URL -->
        <div class="mb-2"><span><strong>{{ $t('content-fetch.titles.url') }}</strong></span></div>
        <b-form-group label-for="url">
          <b-input-group>
            <input
              id="url"
              type="url"
              class="form-control"
              :placeholder="$t('content-fetch.placeholders.get-content')"
              required
              v-model="form.url"
              :class="{ 'is-invalid': typeof formValid.url === 'string' }"
              @input="onInputURL"
              @paste="fetchContent"
            />
            <b-input-group-append>
              <b-button variant="primary" @click="fetchContent" :disabled="loading">{{ $t('content-fetch.buttons.get-content') }}</b-button>
            </b-input-group-append>
          </b-input-group>
          <div v-if="typeof formValid.url === 'string'" class="invalid-feedback">
            <template v-if="formValid.url === 'EMPTY'">Please enter a URL</template>
            <template v-else-if="formValid.url === 'INVALID'">Please enter a valid URL</template>
            <template v-else>{{ formValid.url }}</template>
          </div>
        </b-form-group>
        <p v-if="prefetchError" class="mt-3">{{ $t('content-fetch.invalid.content-not-imported') }}</p>
        <p v-else-if="!prefetchError && form.url && (!form.title || !form.content)" class="mt-3">{{ $t('content-fetch.hints.content-prefill') }}</p>
      </b-col>
    </b-row>

    <span @click="toggleFullForm()" class="text-primary link-styling">
      {{ showFullForm ? $t('content-fetch.buttons.create-content-from-url') : $t('content-fetch.buttons.create-content-manually') }}
    </span>

    <b-row v-if="(showFullForm && !loading) || form.url" class="pt-lg-4">

      <!-- CONTENT -->
      <b-col lg="8">
        <b-row class="px-3">
          <b-col class="d-flex justify-content-between align-items-center px-0 pb-2 mb-2 border-bottom">
            <span><strong>{{ $t('content-fetch.titles.content') }}</strong></span>
            <b-button
              :variant="editContent ? 'primary' : 'outline-primary'"
              class="px-3"
              size="sm"
              @click="editContent = !editContent"
              v-show="form.content"
            >{{ editContent ? $t('content-fetch.buttons.preview') : $t('content-fetch.buttons.edit') }}</b-button>
          </b-col>
        </b-row>
        <b-row>
          <b-col>

            <!-- Title -->
            <h2 v-show="!editContent">{{ form.title }}</h2>
            <b-form-group class="mt-2" v-show="editContent">
              <b-form-input id="title" type="text" :placeholder="$t('content-fetch.placeholders.title')" required v-model="form.title"
                :class="{ 'is-invalid': form.title && form.title.length < 3 }" style="font-size: 18px; font-weight: bold" />
              <div v-if="form.title && form.title.length < 3" class="invalid-feedback">
                {{ $t('content-fetch.invalid.title-short') }}
              </div>
            </b-form-group>

            <!-- Content -->
            <rich-text-field v-if="editContent" v-model="form.content"
              :placeholder="$t('content-fetch.placeholders.description')" />
            <rich-text-content v-else :value="form.content"/>
          </b-col>
        </b-row>
      </b-col>

      <!-- HIGHLIGHT -->
      <b-col lg="4" class="pl-2 pl-lg-4">
        <b-row class="px-3">
          <b-col class="d-flex justify-content-between align-items-center px-0 pb-2 mb-2 border-bottom">
            <span><strong>{{ $t('content-fetch.titles.highlight') }}</strong></span>
            <b-button
              :variant="editHighlight ? 'primary' : 'outline-primary'"
              class="px-3"
              size="sm"
              @click="editHighlight = !editHighlight"
              v-show="form.title && form.description"
            >{{ editHighlight ? $t('content-fetch.buttons.preview') : $t('content-fetch.buttons.edit') }}</b-button>
          </b-col>
        </b-row>
        <b-row class="px-3">
          <content-card
            v-show="!editHighlight"
            :isPreview="true"
            :hideFooter="true"
            :entry="form"
          />
          <content-highlight-editor-form
            v-show="editHighlight"
            :value="{
              description: form.description,
              image: form.image,
              imageURL: form.imageURL
            }"
            @input="({ description, image, imageURL }) => {
              form.description = description;
              form.image = image;
              form.imageURL = imageURL;
            }"
          />
        </b-row>

        <!-- BUTTONS -->
        <b-row class="mt-4 px-3">
          <b-col cols="12" class="text-right px-0">
            <b-form-group>
              <b-button v-if="buttons.includes('DELETE')" variant="danger" size="md" @click.prevent="$emit('deleteSelectedContentShare')">{{ $t('rewards.statuses.delete') }}</b-button>
              <b-button v-if="buttons.includes('ARCHIVE')" size="md" @click.prevent="$emit('disableSelectedContentShare')">{{ $t('content-fetch.buttons.archive') }}</b-button>
              <b-button v-if="buttons.includes('SAVE_DRAFT')" size="md" :disabled="!changed || !isFormValid" @click.prevent="submitInput('INACTIVE')">{{ $t('content-fetch.buttons.save-draft') }}</b-button>
              <b-button v-if="buttons.includes('PUBLISH')" variant="success" size="md" @click.prevent="$emit('publishSelectedContentShare')">{{ $t('content-fetch.buttons.publish') }}</b-button>
              <b-button v-if="buttons.includes('SAVE')" size="md" :disabled="!changed || !isFormValid" @click.prevent="submitInput()" variant="primary">{{ $t('content-fetch.buttons.save') }}</b-button>
              <b-button v-if="buttons.includes('PUBLISH_AND_SAVE')" size="md" :disabled="!changed || !isFormValid" @click.prevent="submitInput()" variant="success">{{ $t('content-fetch.buttons.save-publish') }}</b-button>
            </b-form-group>
          </b-col>
        </b-row>
      </b-col>

    </b-row>

    <b-row class="p-5" v-if="loading">
      <b-col class="p-5 d-flex justify-content-center">
        <b-spinner :style="{ height: '32px', width: '32px' }" type="grow" variant="primary"/>
      </b-col>
    </b-row>

  </b-form>
</template>

<script>
import enumsMixin from '@/mixins/enums';
import formValidationMixin from '@src/mixins/formValidation';
import AssetBlock from '@/components/settings/AssetBlock';
import ContentCard from '@/components/content-share/ContentCard';
import ContentHighlightEditorForm from '@/components/content-share/ContentHighlightEditorForm';
import RichTextField, { RichTextContent } from '@/components/RichTextField';

export default {
  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
    loading: {
      type: Boolean,
      default: false,
    },
    prefetchError: {
      default: null,
    },
  },
  components: {
    AssetBlock,
    ContentCard,
    RichTextField,
    ContentHighlightEditorForm,
    RichTextContent,
  },
  mixins: [
    enumsMixin,
    formValidationMixin,
  ],
  data: () => ({
    form: {},
    urlInputTimer: null,
    editContent: false,
    editHighlight: false,
    showFullForm: false,
  }),
  created() {
    this.onResetForm();
  },
  formValidation: {
    title(value) {
      if (value && value.length <= 2) {
        return 'TOO_SHORT';
      } else if (value === '') {
        return 'EMPTY';
      } else {
        return true;
      }
    },
    url(value) {
      if (typeof value === 'string' && value === '') {
        return 'EMPTY';
      } else if (typeof value === 'string' && !value.match(/(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)/g)) {
        return 'INVALID';
      } else {
        return true;
      }
    },
  },
  computed: {
    changed() {
      let value = {};
      if (this.value) {
        const { title, url, description, image, content } = this.value;
        value = { title, url, description, image, content };
      }
      return JSON.stringify(value) !== JSON.stringify(this.form);
    },
    isFormValid() {
      const invalidInputs = Object.keys(this.formValid).filter(key => this.formValid[key] !== true);
      if (invalidInputs.length) return false;
      return true;
    },
    buttons() {
      const { value, changed } = this;
      if (!value || !value.id) {
        // return [ 'SAVE_DRAFT', 'PUBLISH_AND_SAVE' ];
        return [ 'PUBLISH_AND_SAVE' ];
      }
      if (value && value.status === 'ACTIVE') {
        return [ 'SAVE', 'ARCHIVE' ];
      }
      if (value && value.status === 'INACTIVE') {
        // return [ changed ? 'PUBLISH_AND_SAVE' : 'PUBLISH', 'SAVE_DRAFT', 'DELETE' ];
        return [ changed ? 'PUBLISH_AND_SAVE' : 'PUBLISH', 'DELETE' ];
      }
      return [];
    }
  },
  methods: {
    testLog() {
      console.log('showFullForm IS: ', this.showFullForm);
    },
    toggleFullForm() {
      this.showFullForm = !this.showFullForm;
      this.editHighlight = !this.editHighlight;
      this.editContent = !this.editContent;
    },
    onResetForm() {
      if (this.value) {
        console.log('VALUE: ', this.value);
        const { title, url, description, imageURL, content } = this.value;
        if (title && description) {
          this.editHighlight = false;
        }
        if (content) {
          this.editContent = false;
        }
        this.form = {
          title: title || null,
          url: url || null,
          description: description || null,
          imageURL: imageURL || null,
          content: content || null,
          image: null
        };
      }
    },
    submitInput(status) {
      if ((!this.changed && !this.value.id) || !this.isFormValid) return;

      const input = JSON.parse(JSON.stringify(this.form));

      if (status) {
        input.status = status;
      } else {
        input.status = 'ACTIVE';
      }

      if (input.image && input.image.id) {
        input.image = { id: input.image.id };
      } else {
        delete input.image;
      }

      if (input.description === '') input.description = null;
      this.$emit('input', input);
    },
    fetchContent() {
      if (typeof this.formValid.url !== 'string') {
        this.$emit('fetchContent', this.form.url);
      }
    },
    onInputURL() {
      this.$emit('dismissPrefetchError');
      this.form = {
        title: null,
        url: this.form.url,
        description: null,
        imageURL: null,
        content: null,
        image: null
      };
      if (!this.form.title || !this.form.description) {
        this.editContent = true;
        this.editHighlight = true;
      }
    }
  },
  watch: {
    value() {
      this.onResetForm();
    },
    prefetchError() {
      this.editContent = true;
      this.editHighlight = true;
    },
  },
};
</script>

<style lang="scss">
#title::placeholder {
  color: #aaa;
  font-weight: 400;
  opacity: 1;
}
.link-styling {
  cursor: pointer;
  text-decoration: underline;
  color: #001DC0;
}
.link-styling:hover {
  font-weight: 700;
}
</style>



// WEBPACK FOOTER //
// src/components/content-share/ContentShareFetch.vue